import React, { useState, useEffect, useRef } from "react";
import { C } from "../constants";
import Btn from "./Btn";
import DuplicateStatementNotice from "./DuplicateStatementNotice";
import { FancySelect, FancyTextarea } from "./FormControls";
import {
  INNOVATION_TECHNOLOGIES as DEFAULT_TECH,
  INDUSTRY_VERTICALS as DEFAULT_VERTICALS,
  PROBLEM_FRAMEWORKS,
} from "../constants/snsThrustAreas";
import {
  validateProblemFramework,
  rewriteProblemFramework,
  uploadFiles,
  getYCInspiration,
  getThrustAreas,
  getProblemStatements,
  quickCreateProblemStatement,
} from "../api";
import { formatApiError } from "../utils/apiError";
import {
  fingerprintStatement,
  claimLocalFingerprint,
  fingerprintsFromRecords,
  isStatementTaken,
  loadLocalUsedFingerprints,
  extractProvenance,
  matchInspiration,
  sortProblemOptions,
} from "../utils/problemStatementMeta";

function WeeklyOutcomesBlock({ weeks, totalWeeks, title = "Expected outcomes" }) {
  if (!Array.isArray(weeks) || !weeks.length) return null;
  return (
    <div style={{ margin: "8px 0 4px", padding: 12, borderRadius: 8, background: "#fff", border: "1px solid #D1FAE5" }}>
      {title && <p style={{ fontWeight: 800, fontSize: 12, color: "#047857", marginBottom: 10 }}>
        {title}
        {totalWeeks ? ` · ${totalWeeks} week${totalWeeks === 1 ? "" : "s"}` : ""}
      </p>}
      {weeks.map((w, i) => (
        <div
          key={w.week || i}
          style={{
            marginBottom: i === weeks.length - 1 ? 0 : 10,
            paddingBottom: i === weeks.length - 1 ? 0 : 10,
            borderBottom: i === weeks.length - 1 ? "none" : "1px solid #E6F4EE",
          }}
        >
          <p style={{ fontWeight: 800, fontSize: 12, color: "#1A2333", margin: "0 0 6px" }}>
            Week {w.week}
            {w.days ? ` · ${w.days}` : ""}
          </p>
          <p style={{ fontSize: 13, color: "#1A2333", lineHeight: 1.45, margin: "0 0 4px" }}>
            <span style={{ fontWeight: 800, color: "#0F766E" }}>Technical — </span>
            {w.technical || w.outcome}
          </p>
          <p style={{ fontSize: 13, color: "#1A2333", lineHeight: 1.45, margin: 0 }}>
            <span style={{ fontWeight: 800, color: "#143B56" }}>Outcome — </span>
            {w.outcome || w.technical}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * Verify uploaded/pasted PS against (YC OR Unicorn) + any SNS thrust-area match,
 * report Contains AI Yes/No, or generate via YC/Unicorn RAG + LLM.
 */
export default function ProblemFrameworkPanel({
  problemText,
  onApplyStatement,
  onVerified,
  files,
  setTextInputs,
  numDays = 5,
  teamLabel = "",
}) {
  const who = String(teamLabel || "").trim();
  const [tab, setTab] = useState("verify"); // verify | generate
  const [framework, setFramework] = useState("all");
  const [techs, setTechs] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [sortBy, setSortBy] = useState("relevance");
  const [optionCount, setOptionCount] = useState(5);
  const [seed, setSeed] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [pickedCandidate, setPickedCandidate] = useState(0);
  const [pickedIdea, setPickedIdea] = useState(-1);
  const [usedFingerprints, setUsedFingerprints] = useState(() => loadLocalUsedFingerprints());
  const [loading, setLoading] = useState(false);
  const rewriteVariation = useRef(0);
  const [report, setReport] = useState(null);
  const [rewrite, setRewrite] = useState(null);
  const [previewOutcomes, setPreviewOutcomes] = useState([]);
  const [previewWeeks, setPreviewWeeks] = useState(0);
  const [error, setError] = useState("");
  const [techList, setTechList] = useState(DEFAULT_TECH);
  const [verticalList, setVerticalList] = useState(DEFAULT_VERTICALS);
  const [ycIdeas, setYcIdeas] = useState([]);
  const [ycMeta, setYcMeta] = useState(() => {
    const now = new Date().getFullYear();
    return {
      years: Array.from({ length: now - 2004 }, (_, i) => now - i),
      presentYear: now,
    };
  });
  const [ycFilters, setYcFilters] = useState({
    mode: "all",
    kind: "all",
    year: "",
    window: "recent",
  });
  const problemFiles = (files && files.problem) || [];

  // Always prefer live backend revised catalog for verify / generate dropdowns
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getThrustAreas();
        if (cancelled || !res?.success) return;
        const techs = (res.innovationTechnologies || [])
          .map((t) => (typeof t === "string" ? t : t?.name))
          .filter(Boolean);
        const verts = (res.industryVerticals || [])
          .map((v) => (typeof v === "string" ? v : v?.name))
          .filter(Boolean);
        if (techs.length) setTechList(techs);
        if (verts.length) setVerticalList(verts);
      } catch (_) {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getProblemStatements();
        if (cancelled) return;
        const fps = [
          ...loadLocalUsedFingerprints(),
          ...fingerprintsFromRecords(res?.data || res?.problemStatements || []),
        ];
        setUsedFingerprints(Array.from(new Set(fps)));
      } catch {
        setUsedFingerprints(loadLocalUsedFingerprints());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Always refresh expected outcomes for whatever statement is in the box
  // (paste, verify, or after applying a generated statement).
  useEffect(() => {
    const text = String(problemText || "").trim();
    if (text.length < 24) {
      setPreviewOutcomes([]);
      setPreviewWeeks(0);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await validateProblemFramework(text, false, {
          numDays,
          year: ycFilters.year || "",
          kind: ycFilters.kind || "all",
          window: ycFilters.window || "recent",
        });
        if (cancelled || !res?.success) return;
        const weeks = res.report?.weeklyOutcomes || [];
        setPreviewOutcomes(weeks);
        setPreviewWeeks(res.report?.totalWeeks || weeks.length || 0);
      } catch (_) {
        /* keep last preview */
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [problemText, numDays, ycFilters.year, ycFilters.kind, ycFilters.window]);

  const resolveText = async () => {
    let text = String(problemText || "").trim();
    if (problemFiles.length) {
      const extracted = await uploadFiles({ problem: problemFiles });
      const chunks = (extracted?.extracted?.problem || [])
        .map((x) => x.content)
        .filter(Boolean);
      if (chunks.length) {
        const merged = chunks.join("\n\n").trim();
        text = text ? `${text}\n\n${merged}` : merged;
        if (setTextInputs && merged) {
          setTextInputs((p) => ({
            ...p,
            problem: String(p.problem || "").trim()
              ? `${String(p.problem).trim()}\n\n${merged}`
              : merged,
          }));
        }
      }
    }
    return text;
  };

  const runValidate = async () => {
    setLoading(true);
    setError("");
    setRewrite(null);
    setCandidates([]);
    try {
      const text = await resolveText();
      if (!text.trim()) {
        setError("Paste a statement or upload a file first.");
        setLoading(false);
        return;
      }
      const res = await validateProblemFramework(text, false, {
        numDays,
        year: ycFilters.year || "",
        kind: ycFilters.kind || "all",
        window: ycFilters.window || "recent",
      });
      if (res.success) {
        const assessment = res.report?.assessment || null;
        setReport(res.report || {});
        if (res.report?.weeklyOutcomes?.length) {
          setPreviewOutcomes(res.report.weeklyOutcomes);
          setPreviewWeeks(res.report.totalWeeks || res.report.weeklyOutcomes.length);
        }
        const catTech = (res.catalogs?.innovationTechnologies || [])
          .map((t) => (typeof t === "string" ? t : t?.name))
          .filter(Boolean);
        const catVert = (res.catalogs?.industryVerticals || [])
          .map((v) => (typeof v === "string" ? v : v?.name))
          .filter(Boolean);
        if (catTech.length) setTechList(catTech);
        if (catVert.length) setVerticalList(catVert);
        if (assessment?.status === "ready-for-expert-review") onVerified?.();
      } else setError(formatApiError(res.error, "Validation failed"));
    } catch (e) {
      const msg = formatApiError(e, "Validation failed");
      setError(/token|session|log in/i.test(msg) ? `${msg} — open Login and sign in again, then retry.` : msg);
    }
    setLoading(false);
  };

  const collectUsed = async () => {
    let fps = [...usedFingerprints, ...loadLocalUsedFingerprints()];
    try {
      const res = await getProblemStatements();
      fps = fps.concat(fingerprintsFromRecords(res?.data || res?.problemStatements || []));
    } catch {
      /* local list is enough */
    }
    const uniq = Array.from(new Set(fps.filter(Boolean)));
    setUsedFingerprints(uniq);
    return uniq;
  };

  const cleanRewriteText = (value) => {
    let statement = String(value || "").replace(/\r/g, "\n").trim();
    // Older backend responses append generic product-planning text to the
    // user's statement. Remove those known additions before showing or saving it.
    statement = statement
      .replace(/^\s*(?:research and evaluation|rewritten statement|problem statement)\s*/i, "")
      .replace(/^\s*(?:company|year)\s*:\s*[^\n]*(?:\n|$)/gim, "")
      .replace(/^\s*(?:develop a complete product for this problem|build a usable product around this need|create an end-to-end solution for this problem)\s*:\s*/i, "");
    const appendedGuidance = /\b(?:focus on the main user journey and deliver a working demonstration|define the affected users, build the essential workflow, and test that they can complete it|define the user needs and acceptance criteria from evidence before implementation|address the stated problem by designing, building, and evaluating a complete product for the people affected)\b/i;
    const guidanceMatch = appendedGuidance.exec(statement);
    if (guidanceMatch) statement = statement.slice(0, guidanceMatch.index).trim();
    statement = statement.replace(/\s+/g, " ").trim();
    // If a previous rewrite was passed back in, peel repeated known wrappers.
    let previous;
    do {
      previous = statement;
      statement = statement.replace(/^\s*(?:develop a complete product for this problem|build a usable product around this need|create an end-to-end solution for this problem)\s*:\s*/i, "").trim();
    } while (statement !== previous);
    return statement;
  };

  const normalizeCandidates = (payload, cleanRewrite = false) => {
    const raw = Array.isArray(payload?.options) && payload.options.length
      ? payload.options
      : Array.isArray(payload?.candidates) && payload.candidates.length
        ? payload.candidates
        : payload?.rewritten ? [payload.rewritten] : [];
    const seen = new Set();
    return raw
      .map((item, i) => {
        if (!item) return null;
        if (typeof item === "string") {
          return { id: `opt-${i}`, title: "", statement: cleanRewrite ? cleanRewriteText(item) : item };
        }
        return {
          ...item,
          id: item.id || item._id || `opt-${i}`,
          statement: cleanRewrite
            ? cleanRewriteText(item.statement || item.description || item.text || "")
            : item.statement || item.description || item.text || "",
        };
      })
      .filter((item) => {
        const statement = String(item?.statement || "").trim();
        const key = statement.toLowerCase().replace(/\s+/g, " ");
        if (statement.length <= 20 || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const runRewrite = async (mode) => {
    setLoading(true);
    setError("");
    if (mode === "generate") {
      setReport(null);
      setCandidates([]);
      setPickedCandidate(0);
    } else {
      setRewrite(null);
      setReport(null);
      setCandidates([]);
      setPickedCandidate(0);
    }
    try {
      const text = mode === "generate" ? "" : await resolveText();
      if (mode !== "generate" && !String(text || "").trim() && !seed.trim()) {
        setError("Need a statement to rewrite, or switch to Generate.");
        setLoading(false);
        return;
      }
      // Rewrite is an edit of the learner's own input; reusing the same
      // problem is expected. Uniqueness filtering is only for generated ideas.
      const used = mode === "generate" ? await collectUsed() : [];
      const baseOpts = {
        techs,
        verticals,
        tech: techs[0] || "",
        vertical: verticals[0] || "",
        mode,
        variation: mode === "rewrite" ? rewriteVariation.current++ : 0,
        seed: mode === "generate" ? seed : "",
        numDays,
        uniqueOnly: mode === "generate",
        excludeAssigned: mode === "generate",
        excludeFingerprints: used,
        sortBy,
        count: mode === "generate" ? optionCount : 3,
        year: ycFilters.year || "",
        kind: ycFilters.kind || "all",
        requireYear: true,
        detailed: false,
        minWords: 60,
      };
      const res = await rewriteProblemFramework(text || seed, framework, baseOpts);
      if (!res.success) {
        setError(formatApiError(res.error, "Generation failed"));
        setLoading(false);
        return;
      }
      let options = normalizeCandidates(res, mode === "rewrite").filter(
        (o) => mode !== "generate" || !isStatementTaken(o.statement, used)
      );
      if (mode === "generate" && options.length < optionCount) {
        const extraUsed = [...used, ...options.map((o) => fingerprintStatement(o.statement))];
        for (let variation = 2; variation <= optionCount + 1 && options.length < optionCount; variation += 1) {
          const row = await rewriteProblemFramework(text || seed, framework, {
            ...baseOpts,
            count: Math.max(1, optionCount - options.length),
            variation,
            excludeFingerprints: extraUsed,
          }).catch(() => null);
          normalizeCandidates(row || {}).forEach((o) => {
            if (isStatementTaken(o.statement, extraUsed)) return;
            extraUsed.push(fingerprintStatement(o.statement));
            options.push(o);
          });
        }
      }
      options = sortProblemOptions(options, sortBy);
      if (!options.length) {
        setError("No unused problem statements for these filters. Change tech/industry and try again.");
        setLoading(false);
        return;
      }
      setCandidates(options);
      setRewrite(options[0]);
      setPickedCandidate(0);
      setTab(mode === "generate" ? "generate" : "verify");
      if (options[0]?.weeklyOutcomes?.length) {
        setPreviewOutcomes(options[0].weeklyOutcomes);
        setPreviewWeeks(options[0].totalWeeks || options[0].weeklyOutcomes.length);
      }
    } catch (e) {
      setError(formatApiError(e, "Generation failed"));
    }
    setLoading(false);
  };

  // Duplicate reminder state: { matches, item, statement } while the card is open
  const [dupNotice, setDupNotice] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);

  const finishApply = (statement) => {
    onApplyStatement?.(statement);
    onVerified?.();
  };

  const applyStatement = async (item) => {
    const statement = String(item?.statement || item || "").trim();
    if (!statement) return;
    const provenance = extractProvenance(item, matchInspiration(statement, ycIdeas));
    const fp = claimLocalFingerprint(statement, provenance);
    setUsedFingerprints((prev) => Array.from(new Set([...prev, fp])));
    setError("");
    try {
      await quickCreateProblemStatement(
        item?.title || statement.slice(0, 80),
        statement,
        {
          fingerprint: fp,
          // A learner may intentionally reuse the same problem statement.
          allowDuplicate: true,
          sourceKind: provenance.sourceKind,
          company: provenance.company,
          year: provenance.year,
        }
      );
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.code === "DUPLICATE_STATEMENT") {
        // Still let the learner use this statement in their current plan.
      }
      // Any other save error should not stop the user from using the statement
    }
    setDupNotice(null);
    finishApply(statement);
  };

  const useDuplicateAnyway = async () => {
    if (!dupNotice) return;
    setDupBusy(true);
    try {
      await applyStatement(dupNotice.item);
    } finally {
      setDupBusy(false);
    }
  };

  const loadInspiration = async (refresh = false, filters = ycFilters, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const res = await getYCInspiration(refresh, filters);
      if (res.success) {
        setYcIdeas(res.data || []);
        setYcMeta({
          isLive: res.isLive,
          date: res.generatedDate,
          years: res.years || [],
          classicCount: res.classicCount || 0,
          mode: res.mode,
        });
      } else if (!silent) setError(formatApiError(res.error, "Could not load YC inspiration"));
    } catch (e) {
      if (!silent) setError(formatApiError(e, "Could not load YC inspiration"));
    }
    if (!silent) setLoading(false);
  };

  const updateYcFilters = (next) => {
    setYcFilters(next);
    loadInspiration(false, next);
  };

  const liveOutcomes = (() => {
    if (Array.isArray(rewrite?.weeklyOutcomes) && rewrite.weeklyOutcomes.length) {
      return {
        weeks: rewrite.weeklyOutcomes,
        totalWeeks: rewrite.totalWeeks,
        title: rewrite.mode === "generate" ? "Expected outcomes" : "Expected outcomes",
      };
    }
    if (Array.isArray(report?.weeklyOutcomes) && report.weeklyOutcomes.length) {
      return { weeks: report.weeklyOutcomes, totalWeeks: report.totalWeeks, title: "Expected outcomes" };
    }
    if (previewOutcomes.length) {
      return { weeks: previewOutcomes, totalWeeks: previewWeeks, title: "Expected outcomes" };
    }
    return null;
  })();

  const hasText = !!(String(problemText || "").trim() || problemFiles.length);
  return (
    <div style={{
      marginTop: 14,
      padding: 14,
      borderRadius: 10,
      background: "#fff",
      border: "1px solid #E5E8EF",
    }}>
      <style>{INSPIRE_CSS}</style>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#1A2333", margin: 0 }}>
            Problem statement{who ? ` · ${who}` : ""}
          </p>
          <p style={{ fontSize: 12, color: "#6B7285", margin: "4px 0 0" }}>
            Check whether the statement includes the key details, or create focused rewrite options while preserving the problem you supplied.
          </p>
        </div>
        <div style={{
          display: "inline-flex", padding: 3, borderRadius: 8,
          background: "#F3F4F6", border: "1px solid #E5E8EF", gap: 2,
        }}>
          {[
            { id: "verify", label: "Verify" },
            { id: "generate", label: "Generate new" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setError("");
                setCandidates([]);
                setRewrite(null);
                setPickedCandidate(0);
                if (t.id === "generate") setReport(null);
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                background: tab === t.id ? "#fff" : "transparent",
                color: tab === t.id ? C.primaryDark : "#6B7285",
                boxShadow: tab === t.id ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "generate" && <div style={{
        marginBottom: 12,
        padding: 12,
        borderRadius: 10,
        background: "#F8FAFC",
        border: "1px solid #E5E8EF",
      }}>
        <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 800, color: "#334155" }}>
          Filters — for generated ideas only
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 10,
        }}>
          <FancySelect tone="blue" icon="ti-layout-grid" label="Framework" value={framework} onChange={(e) => setFramework(e.target.value)}>
            {PROBLEM_FRAMEWORKS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </FancySelect>
          <FancySelect
            tone="coral"
            icon="ti-rocket"
            label="Source"
            value={ycFilters.kind}
            onChange={(e) => updateYcFilters({ ...ycFilters, kind: e.target.value })}
          >
            <option value="all">Y Combinator + Unicorn</option>
            <option value="yc">Y Combinator</option>
            <option value="unicorn">Unicorn Company</option>
          </FancySelect>
          <FancySelect
            tone="slate"
            icon="ti-calendar"
            label="Year"
            value={ycFilters.year || ""}
            onChange={(e) => updateYcFilters({ ...ycFilters, year: e.target.value })}
          >
            <option value="">Any year</option>
            {(ycMeta?.years?.length
              ? ycMeta.years
              : Array.from({ length: new Date().getFullYear() - 2004 }, (_, i) => new Date().getFullYear() - i)
            ).map((y) => (
              <option key={y} value={y}>{y}{y === new Date().getFullYear() ? " · present" : ""}</option>
            ))}
          </FancySelect>
          <FancySelect tone="navy" icon="ti-arrows-sort" label="Sort options" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="relevance">Relevance</option>
            <option value="year">Newest year</option>
            <option value="yc">Y Combinator first</option>
            <option value="unicorn">Unicorn first</option>
          </FancySelect>
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 800, color: "#8B94A3", letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Technologies (pick one or more)
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {techList.map((t) => {
            const on = techs.includes(t);
            return (
              <button
                key={t}
                type="button"
                className={`ps-filter-chip${on ? " is-on" : ""}`}
                style={{ background: on ? "#0F766E" : "#fff", color: on ? "#fff" : "#6B7285", borderColor: on ? "#0F766E" : "#E5E8EF" }}
                onClick={() => setTechs((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))}
              >
                {t}
              </button>
            );
          })}
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 800, color: "#8B94A3", letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Industries (pick one or more)
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {verticalList.map((v) => {
            const on = verticals.includes(v);
            return (
              <button
                key={v}
                type="button"
                className={`ps-filter-chip${on ? " is-on" : ""}`}
                style={{ background: on ? "#1B4F72" : "#fff", color: on ? "#fff" : "#6B7285", borderColor: on ? "#1B4F72" : "#E5E8EF" }}
                onClick={() => setVerticals((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))}
              >
                {v}
              </button>
            );
          })}
        </div>
      </div>}

      {tab === "verify" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <Btn small variant="primary" disabled={loading || !hasText} onClick={runValidate}>
            {loading ? "Verifying…" : "Verify"}
          </Btn>
          <Btn small variant="outline" disabled={loading || !hasText} onClick={() => runRewrite("rewrite")}>
            Rewrite to fit
          </Btn>
        </div>
      )}

      {tab === "generate" && (
        <div>
          <FancyTextarea
            tone="slate"
            icon="ti-bulb"
            label="Optional idea"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="Optional seed — or leave blank"
            rows={2}
            style={{ minHeight: 64, marginBottom: 8 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#6B7285" }}>How many options</span>
            {[3, 5, 8].map((n) => (
              <button
                key={n}
                type="button"
                className={`ps-filter-chip${optionCount === n ? " is-on" : ""}`}
                style={{
                  background: optionCount === n ? "#143B56" : "#fff",
                  color: optionCount === n ? "#fff" : "#6B7285",
                  borderColor: optionCount === n ? "#143B56" : "#E5E8EF",
                }}
                onClick={() => setOptionCount(n)}
              >
                {n}
              </button>
            ))}
            <Btn small variant="primary" disabled={loading} onClick={() => runRewrite("generate")}>
              {loading ? "Generating…" : `Generate ${optionCount} statements`}
            </Btn>
            <Btn small variant="outline" disabled={loading} onClick={() => loadInspiration(!!ycIdeas.length)}>
              {ycIdeas.length ? "Refresh inspiration" : "Load inspiration"}
            </Btn>
          </div>
          <p style={{ fontSize: 12, color: "#6B7285", margin: "0 0 10px" }}>
            Filters above shape the set. Select one option, then Update problem statement.
          </p>

          {ycIdeas.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
                  Live inspiration — tap one to seed generate
                </span>
                <button type="button" className={`ps-live-badge ${ycMeta?.isLive ? "is-live" : "is-fallback"}`} onClick={() => loadInspiration(true)}>
                  <span className="ps-live-dot" />
                  {ycMeta?.isLive ? "Live" : "Cached"}
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {sortProblemOptions(ycIdeas, sortBy).slice(0, 10).map((idea, i) => {
                  const meta = extractProvenance({}, idea, `${idea.title || ""} ${idea.source || ""}`);
                  const active = pickedIdea === i;
                  return (
                    <button
                      key={`${idea.title || i}-${i}`}
                      type="button"
                      className={`ps-inspire-card${active ? " is-picked" : ""}`}
                      onClick={() => {
                        setPickedIdea(i);
                        setSeed(idea.title || idea.oneLiner || "");
                        if (meta.year) updateYcFilters({ ...ycFilters, year: String(meta.year) });
                      }}
                      style={{ maxWidth: 280 }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                        <strong style={{ fontSize: 12.5 }}>{idea.title}</strong>
                        {(idea.isLive || meta.isLive) ? <span className="ps-live-pill">LIVE</span> : null}
                      </div>
                      <div className="ps-inspire-meta">
                        {[meta.sourceLabel, meta.company, meta.year].filter(Boolean).join(" · ")}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {candidates.length > 0 && (
            <div style={{ margin: "8px 0 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#1A2333", margin: 0 }}>
                  Choose one of {candidates.length} statements
                </p>
                <Btn
                  small
                  variant="primary"
                  onClick={() => {
                    const opt = candidates[pickedCandidate];
                    if (!opt) return;
                    const meta = extractProvenance(
                      { ...opt, year: opt.year || ycFilters.year },
                      matchInspiration(opt.statement, ycIdeas),
                      `${opt.statement || ""} ${opt.title || ""}`
                    );
                    applyStatement({ ...opt, year: meta.year, company: meta.company });
                  }}
                >
                  Update problem statement
                </Btn>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {candidates.map((opt, i) => {
                  const meta = extractProvenance(
                    { ...opt, year: opt.year || ycFilters.year },
                    matchInspiration(opt.statement, ycIdeas),
                    `${opt.statement || ""} ${opt.title || ""}`
                  );
                  const taken = isStatementTaken(opt.statement, usedFingerprints);
                  const active = pickedCandidate === i;
                  return (
                    <div
                      key={opt.id || i}
                      className={`ps-inspire-card${active ? " is-picked" : ""}`}
                      style={{ opacity: taken ? 0.5 : 1, cursor: taken ? "not-allowed" : "pointer" }}
                      onClick={() => {
                        if (taken) return;
                        setPickedCandidate(i);
                        setRewrite({ ...opt, year: meta.year, company: meta.company, sourceLabel: meta.sourceLabel });
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <input
                          type="radio"
                          name="ps-candidate"
                          checked={active}
                          disabled={taken}
                          onChange={() => {
                            if (taken) return;
                            setPickedCandidate(i);
                            setRewrite({ ...opt, year: meta.year, company: meta.company, sourceLabel: meta.sourceLabel });
                          }}
                          style={{ marginTop: 4 }}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <strong>{`Option ${i + 1}`}</strong>
                          {opt.title ? ` · ${opt.title}` : ""}
                          {taken ? " — already assigned" : ""}
                          <OriginStrip meta={meta} />
                          <div className="ps-inspire-work" style={{ whiteSpace: "pre-wrap" }}>
                            {opt.statement}
                          </div>
                          {!taken && (
                            <div style={{ marginTop: 10 }}>
                              <Btn
                                small
                                variant={active ? "primary" : "outline"}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPickedCandidate(i);
                                  applyStatement({ ...opt, year: meta.year, company: meta.company });
                                }}
                              >
                                Use this statement
                              </Btn>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {dupNotice && (
        <DuplicateStatementNotice
          matches={dupNotice.matches}
          busy={dupBusy}
          onUseSame={useDuplicateAnyway}
          onChooseDifferent={() => setDupNotice(null)}
          onDismiss={() => setDupNotice(null)}
        />
      )}
      {error && (
        <div style={{ fontSize: 12.5, color: "#B91C1C", marginBottom: 8, padding: "8px 10px", background: "#FEF2F2", borderRadius: 8 }}>
          {error}
        </div>
      )}

      {tab === "verify" && report && (() => {
        const assessment = report.assessment || { checks: [], completeCount: 0, total: 0, status: "needs-detail", limitation: "This check reviews completeness only." };
        const checks = assessment.checks || [];
        const ready = assessment.status === "ready-for-expert-review";
        return (
          <div style={{
            marginTop: 12,
            borderRadius: 10, border: "1px solid #E5E8EF", background: "#fff", overflow: "hidden",
          }}>
            <div style={{
              display: "flex", gap: 12, alignItems: "center", padding: "12px 14px",
              background: ready ? "#F3FBF7" : "#F8FAFC", borderBottom: "1px solid #E5E8EF",
            }}>
              <div style={{
                minWidth: 52, height: 44, borderRadius: 10, background: "#fff",
                border: `1.5px solid ${ready ? "#0F9F6E" : "#CBD5E1"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 16, color: ready ? "#047857" : "#475569",
              }}>
                {assessment.completeCount}/{assessment.total}
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: ready ? "#047857" : "#334155" }}>
                  {ready ? "Ready for expert review" : "Add the missing details"}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7285" }}>
                  Completeness check only; this does not validate research claims or evidence.
                </p>
              </div>
            </div>
            <div style={{ padding: "10px 14px 14px", display: "grid", gap: 7 }}>
              {checks.map((check) => (
                <div key={check.id} style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 12.5, color: "#334155" }}>
                  <span style={{ color: check.pass ? "#047857" : "#B45309", fontWeight: 800 }}>{check.pass ? "✓" : "○"}</span>
                  <span><strong>{check.label}</strong>{!check.pass && <span style={{ color: "#64748B" }}> — {check.detail}</span>}</span>
                </div>
              ))}
              {assessment.limitation && (
                <p style={{ margin: "5px 0 0", color: "#64748B", fontSize: 11.5 }}>{assessment.limitation}</p>
              )}
              {(report.suggestions || []).length > 0 && !ready && (
                <div style={{ marginTop: 4, padding: 10, borderRadius: 8, background: "#FFFBEB", color: "#854D0E", fontSize: 12 }}>
                  <strong>To strengthen the statement</strong>
                  <ul style={{ margin: "5px 0 0", paddingLeft: 18 }}>{report.suggestions.map((item, i) => <li key={`${i}-${item}`}>{item}</li>)}</ul>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {tab === "verify" && candidates.length > 0 && (
        <section style={{ marginTop: 14 }}>
          <h3 style={{ margin: "0 0 8px", color: "#1A2333", fontSize: 14 }}>Rewrite options</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
            {candidates.map((option, index) => {
              const selected = pickedCandidate === index;
              return (
                <article key={option.id || index} style={{ border: `1.5px solid ${selected ? "#0F766E" : "#E5E8EF"}`, borderRadius: 10, padding: 12, background: selected ? "#F0FDFA" : "#fff" }}>
                  <p style={{ margin: "0 0 12px", color: "#334155", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{option.statement}</p>
                  <div style={{ display: "flex", gap: 7 }}>
                    <Btn small variant={selected ? "primary" : "outline"} onClick={() => { setPickedCandidate(index); setRewrite(option); }}>Preview</Btn>
                    <Btn small variant="primary" onClick={() => applyStatement(option)}>Use this statement</Btn>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {liveOutcomes && !(tab === "verify" && candidates.length > 0) && (
        <details open style={{ marginTop: 12, background: "#fff", border: "1px solid #E5E8EF", borderRadius: 12, padding: "8px 12px" }}>
          <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#334155" }}>
            Week-by-week outcomes
          </summary>
          <WeeklyOutcomesBlock
            title=""
            weeks={liveOutcomes.weeks}
            totalWeeks={liveOutcomes.totalWeeks}
          />
        </details>
      )}
    </div>
  );
}

const INSPIRE_CSS = `
  .ps-inspire-filter { transition: background .15s ease, border-color .15s ease, color .15s ease, transform .15s ease; }
  .ps-inspire-filter:hover { transform: translateY(-1px); border-color: #1B4F72 !important; color: #143B56 !important; background: #E7F0F6 !important; }
  .ps-inspire-card {
    text-align: left; padding: 10px 12px; border-radius: 10px;
    border: 1.5px solid #E5E8EF; background: #FAFBFC; cursor: pointer;
    font-size: 12px; color: #1A2333;
    transition: border-color .15s ease, background .15s ease, transform .15s ease, box-shadow .15s ease;
  }
  .ps-inspire-card:hover, .ps-inspire-card.is-hover {
    border-color: #1B4F72; background: #E7F0F6; transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(27,79,114,.12);
  }
  .ps-inspire-card:focus-visible { outline: 2px solid #1B4F72; outline-offset: 2px; }
  .ps-inspire-card.is-picked { border-color: #0F766E; background: #E6F7F5; box-shadow: 0 0 0 3px #0F766E22; }
  .ps-inspire-meta { color: #6B7285; margin-top: 4px; font-size: 11px; font-weight: 650; }
  .ps-inspire-work { color: #1A2333; margin-top: 6px; font-size: 12px; line-height: 1.45; }
  .ps-inspire-action { margin-top: 8px; font-size: 11px; font-weight: 750; color: #1B4F72; }
  .ps-inspire-card.is-picked .ps-inspire-action { color: #0F766E; }
  .ps-live-pill {
    font-size: 10px; font-weight: 800; letter-spacing: .04em;
    color: #047857; background: #D1FAE5; border-radius: 999px; padding: 2px 7px;
  }
  .ps-live-badge {
    display: inline-flex; align-items: center; gap: 7px;
    border: 1.5px solid #C6E9D9; background: #F3FBF7; color: #047857;
    border-radius: 999px; padding: 6px 10px; font-size: 11px; font-weight: 700;
    cursor: pointer; margin: 0 0 10px; transition: background .15s, border-color .15s, color .15s, transform .15s;
  }
  .ps-live-badge:hover { background: #047857; color: #fff; border-color: #047857; transform: translateY(-1px); }
  .ps-live-badge.is-fallback { border-color: #FECACA; background: #FEF2F2; color: #B91C1C; }
  .ps-live-badge.is-fallback:hover { background: #B91C1C; color: #fff; border-color: #B91C1C; }
  .ps-live-dot {
    width: 8px; height: 8px; border-radius: 50%; background: currentColor;
    box-shadow: 0 0 0 0 currentColor; animation: ps-live-pulse 1.6s ease-out infinite;
  }
  @keyframes ps-live-pulse {
    0% { box-shadow: 0 0 0 0 rgba(4,120,87,.45); }
    70% { box-shadow: 0 0 0 8px rgba(4,120,87,0); }
    100% { box-shadow: 0 0 0 0 rgba(4,120,87,0); }
  }
  .ps-filter-chip {
    border: 1.5px solid #E5E8EF; background: #fff; color: #6B7285;
    border-radius: 999px; padding: 6px 10px; font-size: 11.5px; font-weight: 700;
    cursor: pointer; transition: all .15s ease;
  }
  .ps-filter-chip:hover { transform: translateY(-1px); box-shadow: 0 3px 10px rgba(16,24,40,.08); }
  .ps-filter-chip.is-on { color: #fff; }
  .ps-path-card {
    text-align: left; padding: 12px 14px; border-radius: 12px; cursor: pointer;
    border: 1.5px solid #E5E8EF; background: #fff;
    display: flex; flex-direction: column; gap: 4px;
    transition: border-color .15s, background .15s, transform .15s, box-shadow .15s;
  }
  .ps-path-card span { font-size: 12px; color: #6B7285; }
  .ps-path-card:hover { border-color: #1B4F72; background: #E7F0F6; transform: translateY(-1px); }
  .ps-path-card.is-on { border-color: #1B4F72; background: #E7F0F6; box-shadow: 0 0 0 3px #1B4F7220; }
`;

function OriginStrip({ meta }) {
  const isYc = meta?.sourceKind === "yc" || meta?.sourceKind === "both" || /y combinator|\byc\b/i.test(String(meta?.sourceLabel || ""));
  const rows = [
    ["Source", meta?.sourceLabel || (meta?.sourceKind === "unicorn" ? "Unicorn Company" : meta?.sourceKind === "yc" ? "Y Combinator" : "")],
    ["Company", meta?.company || ""],
    ["Year", meta?.year || ""],
    ...(isYc && meta?.batch ? [["YC batch", meta.batch]] : []),
    ["Working on", meta?.workingOn || ""],
  ].filter(([, v]) => v && v !== "Not verified" && v !== "Year not listed");
  if (!rows.length) return null;
  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 8,
        marginTop: 8,
      }}>
        {rows.map(([k, v]) => (
            <div key={k} style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: "#F8FAFC",
              border: "1px solid #E5E8EF",
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#8B94A3", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                {k}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1A2333", marginTop: 4, lineHeight: 1.35 }}>{v}</div>
            </div>
        ))}
      </div>
      {meta?.originWhy ? (
        <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#334155", lineHeight: 1.45 }}>
          {meta.originWhy}
        </p>
      ) : null}
    </div>
  );
}

function VerifyColumn({ title, items, ok }) {
  return (
    <div style={{
      borderRadius: 10,
      border: `1px solid ${ok ? "#C6E9D9" : "#FECACA"}`,
      background: ok ? "#F3FBF7" : "#FFF8F8",
      padding: 10,
    }}>
      <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 800, color: ok ? "#047857" : "#B91C1C" }}>
        {title}
      </p>
      {items.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "#6B7285" }}>{ok ? "Nothing passed yet." : "No gaps — all items passed."}</p>
      ) : items.map((item) => (
        <div key={item.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
          <span style={{
            width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 1,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: ok ? "#0F9F6E" : "#DC2626", color: "#fff", fontSize: 10, fontWeight: 800,
          }}>
            {ok ? "✓" : "✗"}
          </span>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1A2333" }}>{item.label}</div>
            {item.detail ? <div style={{ fontSize: 11.5, color: "#6B7285", marginTop: 2 }}>{item.detail}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProvenanceFacts({ report, idea, compact = false }) {
  const p = extractProvenance(report || {}, idea);
  const rows = [
    ["Source", p.sourceLabel || (p.sourceKind === "yc" ? "Y Combinator" : p.sourceKind === "unicorn" ? "Unicorn" : "YC or Unicorn")],
    ["Company", p.company],
    ["Year", [p.year, p.batch].filter(Boolean).join(" · ")],
    ["Working on", p.workingOn],
    ["Stage", p.stage],
    ["HQ", p.location],
  ].filter(([, v]) => v);
  if (!rows.length && !p.isLive) return null;
  return (
    <div style={{
      margin: compact ? "0 0 8px" : "0",
      padding: compact ? "8px 0 0" : "10px 16px 4px",
    }}>
      <p style={{
        fontSize: 11, fontWeight: 800, color: "#6B7285",
        textTransform: "uppercase", letterSpacing: "0.04em",
        margin: "0 0 8px",
      }}>
        Origin
        {p.isLive ? " · LIVE web" : ""}
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 8,
      }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{
            padding: "8px 10px",
            borderRadius: 8,
            background: "#F8FAFC",
            border: "1px solid #E5E8EF",
          }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#8B94A3", letterSpacing: "0.04em", textTransform: "uppercase" }}>{k}</div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1A2333", marginTop: 3, lineHeight: 1.35 }}>{v}</div>
          </div>
        ))}
      </div>
      {p.url ? (
        <a href={p.url} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: 8, fontSize: 12, fontWeight: 700, color: "#1B4F72" }}>
          Open source
        </a>
      ) : null}
    </div>
  );
}

function GatePill({ ok, label, soft = false }) {
  const bg = soft
    ? (ok ? "#EEF2FF" : "#F3F4F6")
    : (ok ? "#E6F8F1" : "#FEF2F2");
  const color = soft
    ? (ok ? "#4338CA" : "#6B7285")
    : (ok ? "#0F9F6E" : "#DC2626");
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11.5,
      fontWeight: 750,
      padding: "5px 10px",
      borderRadius: 8,
      background: bg,
      color,
      border: `1px solid ${soft ? "#E5E8EF" : (ok ? "#C6E9D9" : "#FECACA")}`,
    }}>
      <span style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: soft ? "transparent" : (ok ? "#0F9F6E" : "#DC2626"),
        color: soft ? color : "#fff",
        fontSize: 10,
        fontWeight: 800,
        lineHeight: 1,
      }}>
        {soft ? (ok ? "AI" : "—") : (ok ? "✓" : "✗")}
      </span>
      {label}
    </span>
  );
}



